/*
  Motureo (W1): mega menu kategorii (snippets/motureo-mega-menu.liquid) i akordeon katalogu w szufladzie telefonu.
  Bez zależności; czyste funkcje (normalizacja tekstu, etykiety kategorii) w assets/motureo-logika.js.
  W HTML jest tylko lista rodzin. Kategorie (assets/motureo-menu.json) i marki aut (assets/motureo-auta.json)
  pobieramy raz, przy pierwszym otwarciu panelu albo katalogu w szufladzie (wynik w pamięci modułu).
  Panel (wzorzec disclosure, bez role="menu"): otwarcie po najechaniu (z opóźnieniem) i kliknięciu; Esc,
  kliknięcie poza panelem i fokus wychodzący z panelu zamykają; strzałki: góra/dół między rodzinami albo
  kategoriami, prawo do kategorii rodziny, lewo z powrotem. Filtr ogranicza listę do pasujących rodzin i kategorii.
  Języki danych (data-lang): bg, pl i ro (rynek RO: nazwy z pól „ro” motureo-menu.json, adresy i marki aut jak pl).
*/

const DESKTOP = window.matchMedia('(min-width: 750px)');
const HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');
const OPEN_DELAY = 120;
const CLOSE_DELAY = 250;

const logic = () => /** @type {any} */ (globalThis).MotureoLogika;

/** @type {Map<string, Promise<any>>} */
const cache = new Map();

/** Jedno pobranie pliku JSON na stronę; błąd nie zostaje w pamięci, więc kolejne otwarcie spróbuje ponownie. */
function loadJson(url) {
  if (!url) return Promise.reject(new Error('brak adresu danych'));
  if (!cache.has(url)) {
    const request = fetch(url, { credentials: 'omit' }).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    });
    request.catch(() => cache.delete(url));
    cache.set(url, request);
  }
  return /** @type {Promise<any>} */ (cache.get(url));
}

/** Kategorie w języku strony. Wersja ro: nazwa z pola „ro” (motureo-logika.js zna tylko bg i pl), adres
    podstawowy jak w wersji pl, bez podpowiedzi z nazwą polską; brak pola „ro” — nazwa polska. */
function categoryEntries(categories, lang) {
  if (lang !== 'ro') return logic().categoryEntries(categories, lang);
  return (Array.isArray(categories) ? categories : []).map((c) => ({
    label: c.ro || c.pl,
    title: '',
    handle: c.handle,
  }));
}

/** Lista <ul> kategorii rodziny (odnośniki do kolekcji; jednakowe nazwy z nazwą polską w title). */
function categoryList(categories, lang, collectionsUrl, className) {
  const list = document.createElement('ul');
  list.className = className;
  list.setAttribute('role', 'list');
  for (const entry of categoryEntries(categories, lang)) {
    const item = document.createElement('li');
    const link = document.createElement('a');
    link.href = `${collectionsUrl}/${encodeURIComponent(entry.handle)}`;
    link.textContent = entry.label;
    if (entry.title) link.title = entry.title;
    item.append(link);
    list.append(item);
  }
  return list;
}

class MotureoMegaMenu extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-mega-toggle]');
    this.panel = this.querySelector('[data-mega-panel]');
    this.body = this.querySelector('[data-mega-body]');
    this.filter = this.querySelector('[data-mega-filter]');
    this.empty = this.querySelector('[data-mega-empty]');
    this.status = this.querySelector('[data-mega-status]');
    if (!this.toggle || !this.panel || !this.body) return;

    this.families = [...this.panel.querySelectorAll('.motureo-mega__family')];
    this.toggle.setAttribute('role', 'button');

    this.toggle.addEventListener('click', (event) => {
      if (!DESKTOP.matches) return;
      event.preventDefault();
      this.isOpen ? this.close(true) : this.open({ focus: event.detail === 0 });
    });
    this.toggle.addEventListener('keydown', (event) => {
      if (event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        this.open({ focus: true });
      }
    });

    if (HOVER.matches) {
      this.toggle.addEventListener('mouseenter', () => this.#schedule(() => this.open(), OPEN_DELAY));
      this.addEventListener('mouseleave', () => {
        if (!this.panel.contains(document.activeElement)) this.#schedule(() => this.close(), CLOSE_DELAY);
      });
      this.addEventListener('mouseenter', () => this.#cancel());
    }

    this.panel.addEventListener('keydown', this.#onKeydown);
    this.panel.addEventListener('focusout', this.#onFocusOut);
    this.panel.querySelector('[data-mega-close]')?.addEventListener('click', () => this.close(true));
    this.panel.addEventListener('mouseover', (event) => {
      const family = event.target.closest?.('.motureo-mega__family');
      if (family && !this.body.hasAttribute('data-filtering')) this.#activate(family);
    });
    this.panel.addEventListener('focusin', (event) => {
      const link = event.target.closest?.('.motureo-mega__family-link');
      if (link && !this.body.hasAttribute('data-filtering')) this.#activate(link.parentElement);
    });
    this.panel.querySelectorAll('button.motureo-mega__family-link').forEach((button) => {
      button.addEventListener('click', () => this.#firstCategory(button.parentElement)?.focus());
    });
    this.filter?.addEventListener('input', () => this.#applyFilter());

    document.addEventListener('click', this.#onDocumentClick);
    DESKTOP.addEventListener('change', () => this.close());
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocumentClick);
  }

  get isOpen() {
    return !this.panel.hidden;
  }

  open({ focus = false } = {}) {
    this.#cancel();
    if (!DESKTOP.matches) return;
    if (!this.isOpen) {
      this.panel.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
      if (!this.panel.querySelector('.motureo-mega__family[data-active]') && this.families[0]) {
        this.#activate(this.families[0]);
      }
      this.#load();
    }
    if (focus) (this.filter || this.#visibleFamilyLinks()[0])?.focus();
  }

  close(returnFocus = false) {
    this.#cancel();
    if (!this.isOpen) return;
    this.panel.hidden = true;
    this.toggle.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-open');
    if (returnFocus) this.toggle.focus();
  }

  #timer = 0;
  #state = 'idle'; // idle → loading → ready | error

  #schedule(fn, delay) {
    this.#cancel();
    this.#timer = window.setTimeout(fn, delay);
  }

  #cancel() {
    window.clearTimeout(this.#timer);
  }

  #setState(state) {
    this.#state = state;
    this.body.setAttribute('aria-busy', String(state === 'loading'));
    const loading = this.querySelector('[data-mega-loading]');
    const error = this.querySelector('[data-mega-error]');
    if (loading) loading.hidden = state !== 'loading';
    if (error) error.hidden = state !== 'error';
  }

  /** Kategorie i marki: jedno pobranie, wstawienie do listy rodzin; błąd → odnośnik do huba katalogu. */
  async #load() {
    if (this.#state === 'loading' || this.#state === 'ready') return;
    if (!logic()) {
      this.#setState('error');
      return;
    }
    this.#setState('loading');
    const lang = this.dataset.lang || 'pl';
    try {
      const menu = await loadJson(this.dataset.src);
      const rodziny = Array.isArray(menu?.rodziny) ? menu.rodziny : [];
      for (const family of this.families) {
        const data = rodziny[Number(family.dataset.family)];
        if (!data?.kategorie?.length || family.querySelector('.motureo-mega__cats')) continue;
        const list = categoryList(data.kategorie, lang, this.dataset.collectionsUrl || '/collections', 'motureo-mega__cats');
        list.dataset.familyTitle = family.querySelector('.motureo-mega__family-link')?.textContent || '';
        family.append(list);
      }
      this.#setState('ready');
      if (this.filter?.value) this.#applyFilter();
    } catch {
      this.#setState('error');
      return;
    }
    // marki są dodatkiem: błąd pobrania zostawia kolumnę ukrytą
    try {
      const auta = await loadJson(this.dataset.makesSrc);
      this.#renderMakes(Array.isArray(auta?.makes) ? auta.makes : [], lang);
    } catch {
      /* bez kolumny marek */
    }
  }

  #renderMakes(makes, lang) {
    const box = this.querySelector('[data-mega-makes]');
    const list = this.querySelector('[data-mega-makes-list]');
    if (!box || !list || list.children.length) return;
    const sorted = [...makes].sort((a, b) => String(a.name).localeCompare(String(b.name)));
    for (const make of sorted) {
      const item = document.createElement('li');
      const link = document.createElement('a');
      const url = new URL(this.dataset.searchUrl || '/search', location.origin);
      url.searchParams.set('q', String(make.q || make.name || ''));
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('options[unavailable_products]', 'last');
      link.href = url.pathname + url.search;
      link.textContent = String((lang === 'bg' ? make.bg : make.name) || make.name || '');
      item.append(link);
      list.append(item);
    }
    box.hidden = list.children.length === 0;
  }

  #onDocumentClick = (event) => {
    if (this.isOpen && !this.contains(event.target)) this.close();
  };

  /** Fokus wychodzi poza panel (Tab za ostatnim, Shift+Tab przed pierwszym albo klik gdzie indziej): zamykamy. */
  #onFocusOut = (event) => {
    const next = /** @type {Node | null} */ (event.relatedTarget);
    if (!this.isOpen || !next || this.panel.contains(next)) return;
    this.close(false);
  };

  #activate(family) {
    if (!family) return;
    for (const item of this.families) {
      if (item === family) item.setAttribute('data-active', '');
      else item.removeAttribute('data-active');
    }
  }

  #firstCategory(family) {
    return family?.querySelector('.motureo-mega__cats a');
  }

  #visibleFamilyLinks() {
    return this.families.filter((f) => !f.hidden).map((f) => f.querySelector('.motureo-mega__family-link'));
  }

  #onKeydown = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close(true);
      return;
    }
    if (event.key === 'Tab') {
      // Shift+Tab z pierwszego elementu (filtr) wraca na przycisk; Tab z ostatniego idzie dalej (focusout zamyka)
      const first = this.panel.querySelector('a[href], button, input');
      if (event.shiftKey && target === first) {
        event.preventDefault();
        this.close(true);
      }
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    if (target === this.filter) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.#visibleFamilyLinks()[0]?.focus();
      }
      return;
    }

    const familyLink = target.closest('.motureo-mega__family-link');
    const catList = target.closest('.motureo-mega__cats, .motureo-mega__makes-list');
    let list = [];
    if (familyLink) list = this.#visibleFamilyLinks();
    else if (catList) list = [...catList.querySelectorAll('a')].filter((a) => !a.closest('[hidden]'));
    if (!list.length) return;
    const index = list.indexOf(target);

    if (event.key === 'ArrowRight' && familyLink) {
      const first = this.#firstCategory(familyLink.parentElement);
      if (first) {
        event.preventDefault();
        first.focus();
      }
      return;
    }
    if (event.key === 'ArrowLeft' && catList) {
      const family = catList.closest('.motureo-mega__family');
      const link = family?.querySelector('.motureo-mega__family-link');
      if (link) {
        event.preventDefault();
        link.focus();
      }
      return;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') return;

    event.preventDefault();
    let next = index;
    if (event.key === 'ArrowDown') next = index < list.length - 1 ? index + 1 : 0;
    if (event.key === 'ArrowUp') next = index > 0 ? index - 1 : list.length - 1;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = list.length - 1;
    list[next]?.focus();
  };

  #applyFilter() {
    const L = logic();
    if (!L) return;
    const query = L.normalizeText(this.filter.value);
    const filtering = query.length >= 2;
    this.body.toggleAttribute('data-filtering', filtering);
    let matches = 0;
    for (const family of this.families) {
      const name = L.normalizeText(family.querySelector('.motureo-mega__family-link')?.textContent);
      const familyMatch = filtering && name.includes(query);
      let visibleCats = 0;
      for (const item of family.querySelectorAll('.motureo-mega__cats li')) {
        const show = !filtering || familyMatch || L.normalizeText(item.textContent).includes(query);
        item.hidden = !show;
        if (show) visibleCats += 1;
      }
      family.hidden = filtering && !familyMatch && visibleCats === 0;
      if (filtering && !family.hidden) matches += Math.max(visibleCats, 1);
    }
    if (this.empty) this.empty.hidden = !filtering || matches > 0;
    if (this.status) {
      this.status.textContent = filtering ? (this.status.dataset.template || '').replace('[n]', String(matches)) : '';
    }
  }
}

if (!customElements.get('motureo-mega-menu')) customElements.define('motureo-mega-menu', MotureoMegaMenu);

/* Szuflada telefonu: akordeon rodzin z tych samych danych (motureo-menu.json), budowany przy pierwszym otwarciu. */
async function buildDrawerCatalog(target) {
  if (target.hasAttribute('data-built') || target.hasAttribute('aria-busy')) return;
  const L = logic();
  if (!L) return;
  target.setAttribute('aria-busy', 'true');
  let menu;
  try {
    menu = await loadJson(target.dataset.src);
  } catch {
    target.removeAttribute('aria-busy');
    const error = document.createElement('p');
    error.className = 'motureo-drawer-catalog__error';
    error.textContent = target.dataset.error || '';
    if (!target.querySelector('.motureo-drawer-catalog__error')) target.append(error);
    return; // zostaje odnośnik „Wszystkie kategorie” do huba katalogu
  }
  target.removeAttribute('aria-busy');
  target.setAttribute('data-built', '');
  const lang = target.dataset.lang || 'pl';
  const base = target.dataset.collectionsUrl || '/collections';
  const list = document.createElement('ul');
  list.className = 'motureo-drawer-catalog__list';
  list.setAttribute('role', 'list');
  let index = 0;
  for (const family of Array.isArray(menu?.rodziny) ? menu.rodziny : []) {
    const name = String(family[lang] || family.pl || '');
    // starszy motureo-logika.js (np. przez chwilę w trakcie wgrywania) nie ma familyHandle: adres podstawowy
    const familyPath = L.familyHandle ? L.familyHandle(family, lang) : family.handle || null;
    const familyUrl = familyPath ? `${base}/${encodeURIComponent(familyPath)}` : null;
    const item = document.createElement('li');
    item.className = 'motureo-drawer-catalog__family';
    if (!family.kategorie?.length) {
      if (!familyUrl) continue;
      const link = document.createElement('a');
      link.href = familyUrl;
      link.textContent = name;
      link.className = 'motureo-drawer-catalog__link';
      item.append(link);
    } else {
      index += 1;
      const id = `MotureoDrawerFamily-${index}`;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'motureo-drawer-catalog__toggle';
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', id);
      button.textContent = name;
      const sub = categoryList(family.kategorie, lang, base, 'motureo-drawer-catalog__cats');
      sub.id = id;
      sub.hidden = true;
      if (familyUrl) {
        const all = document.createElement('li');
        const link = document.createElement('a');
        link.href = familyUrl;
        // L1b: znacznik neutralny [x] (wcześniej [rodzina]; stary nadal działa, gdyby wrócił z panelu języków)
        link.textContent = (target.dataset.allLabel || '[x]').replace('[x]', name).replace('[rodzina]', name);
        all.append(link);
        sub.prepend(all);
      }
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
        sub.hidden = expanded;
      });
      item.append(button, sub);
    }
    list.append(item);
  }
  // cały katalog zwinięty pod jednym przyciskiem, żeby menu główne i wybór języka zostały blisko góry szuflady
  list.id = 'MotureoDrawerCatalogList';
  list.hidden = true;
  const heading = target.querySelector('.motureo-drawer-catalog__heading');
  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'motureo-drawer-catalog__toggle motureo-drawer-catalog__main';
  toggle.setAttribute('aria-expanded', 'false');
  toggle.setAttribute('aria-controls', list.id);
  toggle.textContent = heading?.textContent?.trim() || '';
  toggle.addEventListener('click', () => {
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    list.hidden = expanded;
  });
  heading?.replaceWith(toggle);
  target.querySelector('.motureo-drawer-catalog__error')?.remove();
  target.append(list);
}

const drawer = /** @type {HTMLElement | null} */ (document.querySelector('[data-motureo-drawer-catalog]'));
if (drawer) {
  const details = drawer.closest('details');
  if (details) {
    details.addEventListener('toggle', () => {
      if (details.open) buildDrawerCatalog(drawer);
    });
  } else buildDrawerCatalog(drawer);
}
